import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Check, X, Menu } from 'lucide-react';
import './index.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { getFriendRequests } from './api';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/ConfirmModal';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Dashboard from './pages/Dashboard';
import SystemsList from './pages/SystemsList';
import SystemDetail from './pages/SystemDetail';
import Settings from './pages/Settings';
import Explore from './pages/Explore';
import Friends from './pages/Friends';
import FriendProfile from './pages/FriendProfile';
import ComparisonView from './components/ComparisonView';
import Groups from './pages/Groups';
import GroupDetail from './pages/GroupDetail';
import Admin from './pages/Admin';
import AdminTrailEditor from './pages/AdminTrailEditor';

const NAV_LINKS = [
  { to: '/', label: 'DASHBOARD' },
  { to: '/systems', label: 'TRAIL SYSTEMS' },
  { to: '/explore', label: 'EXPLORE' },
  { to: '/friends', label: 'FRIENDS' },
  { to: '/groups', label: 'GROUPS' },
  { to: '/settings', label: 'SETTINGS' },
];

function NavBar() {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const location = useLocation();

  const navLinks = [
    ...NAV_LINKS,
    ...(user?.isAdmin ? [{ to: '/admin', label: 'ADMIN' }] : []),
  ];

  // Close menu on navigation
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const loadPending = useCallback(() => {
    getFriendRequests()
      .then(res => setPendingCount(res.data.length))
      .catch(() => {});
  }, []);

  useEffect(loadPending, [loadPending, location.pathname]);

  return (
    <nav className="navbar">
      <div className="navbar__bar">
        <NavLink to="/" className="navbar__logo">
          <img src="/notched-logo.svg" alt="Notched" className="navbar__logo-img" />
        </NavLink>
        <div className="navbar__links">
          {navLinks.map(({ to, label }) => (
            <NavLink key={to} to={to} end={to === '/'} className={({ isActive }) => `navbar__link${isActive ? ' navbar__link--active' : ''}`}>
              {label}
              {to === '/friends' && pendingCount > 0 && <span className="navbar__dot" />}
            </NavLink>
          ))}
        </div>
        <div className="flex-1" />
        <button onClick={logout} className="btn btn--secondary btn--sm navbar__logout">
          LOG OUT
        </button>
        <button className="navbar__hamburger" aria-label="Toggle menu" onClick={() => setMenuOpen(prev => !prev)}>
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {menuOpen && <div className="navbar__overlay" onClick={() => setMenuOpen(false)} />}
      <div className={`navbar__mobile-menu${menuOpen ? ' navbar__mobile-menu--open' : ''}`}>
        <button className="navbar__mobile-close" aria-label="Close menu" onClick={() => setMenuOpen(false)}>
          <X size={22} />
        </button>
        {navLinks.map(({ to, label }) => (
          <NavLink key={to} to={to} end={to === '/'} className={({ isActive }) => `navbar__mobile-link${isActive ? ' navbar__mobile-link--active' : ''}`}>
            {label}
            {to === '/friends' && pendingCount > 0 && <span className="navbar__dot" />}
          </NavLink>
        ))}
        <button onClick={logout} className="navbar__mobile-link">
          LOG OUT
        </button>
      </div>
    </nav>
  );
}

function StravaCallbackHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const [stravaModal, setStravaModal] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('strava') === 'connected') {
      setStravaModal('connected');
      navigate('/settings', { replace: true });
    } else if (params.get('strava') === 'error') {
      setStravaModal('error');
      navigate('/settings', { replace: true });
    }
  }, [location, navigate]);

  if (!stravaModal) return null;

  const success = stravaModal === 'connected';

  return (
    <div className="modal__overlay" onClick={() => setStravaModal(null)}>
      <div className="modal__content strava-modal" onClick={e => e.stopPropagation()}>
        <div className={`strava-modal__icon ${success ? 'strava-modal__icon--success' : 'strava-modal__icon--error'}`}>
          {success ? <Check size={28} color="var(--accent)" /> : <X size={28} color="var(--red)" />}
        </div>
        <h2 className="strava-modal__title">
          {success ? 'STRAVA CONNECTED' : 'CONNECTION FAILED'}
        </h2>
        <p className="strava-modal__desc">
          {success
            ? 'Your Strava account is now linked. Sync your recent rides to auto-mark completed trails.'
            : 'Something went wrong connecting to Strava. Please try again.'}
        </p>
        <button
          className={`btn ${success ? 'btn--primary' : 'btn--secondary'} btn--full`}
          onClick={() => setStravaModal(null)}
        >
          {success ? 'GOT IT' : 'CLOSE'}
        </button>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner spinner--lg" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner spinner--lg" />
      </div>
    );
  }

  if (user) return <Navigate to="/" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner spinner--lg" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!user.isAdmin) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public auth routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected app routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <StravaCallbackHandler />
          <NavBar />
          <main className="content-main">
            <Dashboard />
          </main>
        </ProtectedRoute>
      } />
      <Route path="/systems" element={
        <ProtectedRoute>
          <NavBar />
          <main className="content-main">
            <SystemsList />
          </main>
        </ProtectedRoute>
      } />
      <Route path="/systems/:id" element={
        <ProtectedRoute>
          <NavBar />
          <main className="content-main">
            <SystemDetail />
          </main>
        </ProtectedRoute>
      } />
      <Route path="/explore" element={
        <ProtectedRoute>
          <NavBar />
          <main className="content-main">
            <Explore />
          </main>
        </ProtectedRoute>
      } />
      <Route path="/friends" element={
        <ProtectedRoute>
          <NavBar />
          <main className="content-main">
            <Friends />
          </main>
        </ProtectedRoute>
      } />
      <Route path="/friends/:friendId" element={
        <ProtectedRoute>
          <NavBar />
          <main className="content-main">
            <FriendProfile />
          </main>
        </ProtectedRoute>
      } />
      <Route path="/friends/:friendId/compare/:systemId" element={
        <ProtectedRoute>
          <NavBar />
          <main className="content-main">
            <ComparisonView />
          </main>
        </ProtectedRoute>
      } />
      <Route path="/groups" element={
        <ProtectedRoute>
          <NavBar />
          <main className="content-main">
            <Groups />
          </main>
        </ProtectedRoute>
      } />
      <Route path="/groups/:id" element={
        <ProtectedRoute>
          <NavBar />
          <main className="content-main">
            <GroupDetail />
          </main>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <AdminRoute>
          <NavBar />
          <main className="content-main">
            <Admin />
          </main>
        </AdminRoute>
      } />
      <Route path="/admin/trails/:id" element={
        <AdminRoute>
          <NavBar />
          <main className="content-main">
            <AdminTrailEditor />
          </main>
        </AdminRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <NavBar />
          <main className="content-main">
            <Settings />
          </main>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <ToastProvider>
          <ConfirmProvider>
            <AppRoutes />
          </ConfirmProvider>
        </ToastProvider>
      </AuthProvider>
    </Router>
  );
}
